/**
 * Macro #22 — Parcel Utility / Neighbor / Referral API client.
 *
 * All values are backend-authoritative. The frontend NEVER fabricates numbers,
 * earnings or rewards. Referral attribution POST carries the SIWE session +
 * session-bound CSRF (same pattern as the Territory Identity writes).
 */
import { API_BASE } from "./api";
import { getCsrfToken, clearCsrfToken } from "./territory-identity-api";

export class ParcelUtilityError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ParcelUtilityError";
    this.status = status;
    this.code = code;
  }
}

async function parse<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as { code?: string; message?: string; detail?: { code?: string; message?: string } };
    const code = env.code ?? env.detail?.code ?? "UNKNOWN";
    const message = env.message ?? env.detail?.message ?? resp.statusText;
    throw new ParcelUtilityError(resp.status, code, message);
  }
  return body as T;
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    signal,
  });
  return parse<T>(resp);
}

// ---- Types ----
export interface ReferralSummary {
  wallet: string;
  referral_code: string | null;
  referral_enabled: boolean;
  qualified_referral_count: number;
  pending_referral_count: number;
  my_attribution: {
    attributed: boolean;
    qualification_state: string;
    qualified_at: string | null;
  } | null;
}

export interface UtilityParcel {
  parcel_id: number;
  geometry: { x: number; y: number; w: number; h: number };
  area: number;
  tier: string;
  acquired_block: number | null;
  has_basic_identity: boolean;
  identity_id: string | null;
  publishable: boolean;
  published: boolean;
  edge_neighbor_count: number;
  same_owner_edge_neighbor_count: number;
  connected_cluster_id: string | null;
  connected_cluster_size: number;
  connected_cluster_area: number;
}

export interface UtilityCluster {
  cluster_id: string;
  size: number;
  area: number;
  member_parcel_ids: number[];
}

export interface SelfUtility {
  wallet: string;
  owns_land: boolean;
  parcels: UtilityParcel[];
  clusters: UtilityCluster[];
  summary: {
    parcel_count: number;
    total_area: number;
    connected_cluster_count: number;
    largest_cluster_size: number;
    largest_cluster_area: number;
    edge_neighbor_total: number;
  };
  referral: ReferralSummary;
  flags: {
    referral_enabled: boolean;
    has_basic_identity: boolean;
    publishable: boolean;
    published: boolean;
    has_edge_neighbors: boolean;
  };
  first_acquisition: {
    block: number | null;
    kind: string | null;
    timestamp: number | null;
    timestamp_status: string | null;
  } | null;
}

export interface NeighborEntry {
  parcel_id: number;
  adjacency_class: string;
  shared_edge_length: number;
  geometry: { x: number; y: number; w: number; h: number };
  relationship: "self" | "other";
  public_identity: { display_name: string | null; published: boolean } | null;
}

export interface NeighborsResponse {
  parcel_id: number;
  edge_neighbors: NeighborEntry[];
  edge_neighbor_count: number;
  corner_touching_count: number;
}

export interface ReferralAttributionResult {
  referred_wallet: string;
  qualification_state: string;
  created_at: string | null;
}

async function submitReferralCode(code: string): Promise<ReferralAttributionResult> {
  const doPost = async (csrf: string) =>
    fetch(`${API_BASE}/api/v1/artist/self/referral`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
      body: JSON.stringify({ code }),
    });
  let resp = await doPost(await getCsrfToken());
  if (resp.status === 403) {
    const peek = await resp.clone().json().catch(() => null);
    const c = peek?.code ?? peek?.detail?.code;
    if (c === "CSRF_INVALID" || c === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doPost(await getCsrfToken(true));
    }
  }
  return parse<ReferralAttributionResult>(resp);
}

export const parcelUtilityApi = {
  self: (signal?: AbortSignal) => get<SelfUtility>("/api/v1/parcel-utility/self", signal),
  neighbors: (parcelId: number, signal?: AbortSignal) =>
    get<NeighborsResponse>(`/api/v1/parcel-utility/parcels/${parcelId}/neighbors`, signal),
  referral: (signal?: AbortSignal) => get<ReferralSummary>("/api/v1/artist/self/referral", signal),
  submitReferralCode,
};
